import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MyMatch } from '@swiss-magic/shared-types';
import { Button } from '@/components/ui/button';
import { selfReportMatch } from '@/lib/api/player';
import { ApiError } from '@/lib/api/core';
import { showErrorToast, showSuccessToast } from '@/lib/toast';

// Story 010 — inline self-report row. Shown on in-progress matches the
// player can act on (canSelfReport from the API). Player-perspective
// labels — backend translates to first-player score.
export function SelfReportRow({
  tournamentId,
  match,
  hasDraws,
  onReported,
}: {
  tournamentId: string;
  match: MyMatch;
  hasDraws: boolean;
  onReported: () => void;
}) {
  const { t } = useTranslation('player');
  const [submitting, setSubmitting] = useState<'win' | 'draw' | 'loss' | null>(
    null,
  );

  const submit = async (perspective: 'win' | 'draw' | 'loss') => {
    if (submitting) return;
    setSubmitting(perspective);
    try {
      await selfReportMatch(
        tournamentId,
        match.roundNumber,
        match.tableNumber,
        perspective,
      );
      showSuccessToast(t('my_matches.self_report.success'));
      onReported();
    } catch (err) {
      const msg = err instanceof ApiError ? err.userMessage : t('my_matches.self_report.error');
      showErrorToast(msg);
    } finally {
      setSubmitting(null);
    }
  };

  const submittingLabel = t('my_matches.self_report.submitting');
  return (
    <div
      data-testid={`my-match-self-report-${match.roundNumber}`}
      className="mt-1 flex flex-col gap-2 border-t pt-3"
    >
      <p className="text-xs text-muted-foreground">{t('my_matches.self_report.prompt')}</p>
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          className="flex-1"
          data-testid={`my-match-self-report-win-${match.roundNumber}`}
          disabled={submitting !== null}
          onClick={() => submit('win')}
        >
          {submitting === 'win' ? submittingLabel : t('my_matches.self_report.win')}
        </Button>
        {hasDraws && (
          <Button
            size="sm"
            variant="outline"
            className="flex-1"
            data-testid={`my-match-self-report-draw-${match.roundNumber}`}
            disabled={submitting !== null}
            onClick={() => submit('draw')}
          >
            {submitting === 'draw' ? submittingLabel : t('my_matches.self_report.draw')}
          </Button>
        )}
        <Button
          size="sm"
          variant="outline"
          className="flex-1"
          data-testid={`my-match-self-report-loss-${match.roundNumber}`}
          disabled={submitting !== null}
          onClick={() => submit('loss')}
        >
          {submitting === 'loss' ? submittingLabel : t('my_matches.self_report.loss')}
        </Button>
      </div>
    </div>
  );
}
